import React, { useEffect, useMemo, useState } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@mui/material";

import api from "../../services/api/axios";

const COUNTRY_CODE_TO_NAME = {
  US: "United States",
  USA: "United States",
  "UNITED STATES": "United States",
  "UNITED STATES OF AMERICA": "United States",
};

const STATE_CODE_MAP_US = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
};

const STATE_NAME_TO_CODE_US = Object.fromEntries(
  Object.entries(STATE_CODE_MAP_US).map(([code, name]) => [
    name.toUpperCase(),
    code,
  ]),
);

const normalizeCountry = (value) => {
  if (!value) return "";
  return COUNTRY_CODE_TO_NAME[value.toString().trim().toUpperCase()] || value;
};

const normalizeUsStateCode = (value) => {
  if (!value) return "";

  const normalized = value.toString().trim().toUpperCase();
  if (STATE_CODE_MAP_US[normalized]) return normalized;
  return STATE_NAME_TO_CODE_US[normalized] || "";
};

function CountyDropdown({
  country,
  state,
  value,
  onChange,
  label = "County",
  sx = {},
}) {
  const [counties, setCounties] = useState([]);
  const [loading, setLoading] = useState(false);

  const normalizedCountry = useMemo(() => normalizeCountry(country), [country]);
  const stateCode = useMemo(() => normalizeUsStateCode(state), [state]);
  const useDropdown = normalizedCountry === "United States" && Boolean(stateCode);

  useEffect(() => {
    let cancelled = false;

    if (!useDropdown) {
      setCounties([]);
      return undefined;
    }

    setLoading(true);
    api
      .get(`/counties/${stateCode}`)
      .then((res) => {
        if (cancelled) return;
        const next = Array.isArray(res.data)
          ? res.data
              .map((item) => item?.name)
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b))
          : [];
        setCounties(next);
      })
      .catch(() => {
        if (!cancelled) {
          setCounties([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [stateCode, useDropdown]);

  if (!useDropdown || counties.length === 0) {
    return (
      <TextField
        sx={sx}
        label={label}
        value={value || ""}
        onChange={onChange}
        fullWidth
      />
    );
  }

  const normalizedValue = counties.includes(value) ? value : "";

  return (
    <FormControl sx={sx} fullWidth>
      <InputLabel>{label}</InputLabel>
      <Select
        value={normalizedValue}
        label={label}
        onChange={onChange}
        disabled={loading}
      >
        {counties.map((county) => (
          <MenuItem key={county} value={county}>
            {county}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

export default CountyDropdown;
